import { Button, Grid } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { updateAdminUserInfo } from '../../../actions/userActions'
import locations from '../../../data/locations'
import { useDateFormatter } from '../../../hooks/useDateFormatter'
import { useEmailValidator } from '../../../hooks/useEmailValidator'
import { useInput } from '../../../hooks/useInput'
import AutoCompleteSelect from '../../Form/AutoCompleteSelect'
import SelectInput from '../../Form/SelectInput'
import TextInput from '../../Form/TextInput'
import PasswordResetLink from './PasswordResetLink'

interface Props {
  user: User
  setShowEditUser: (val: boolean) => void
  setShowDeactivateModal: (val: boolean) => void
  setShowDeleteModal: (val: boolean) => void
}

const EditAdminUser = ({
  user,
  setShowEditUser,
  setShowDeactivateModal,
  setShowDeleteModal,
}: Props) => {
  const dispatch = useDispatch()

  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)

  const {
    value: firstName,
    bind: bindFirstName,
    error: firstNameError,
    setError: setFirstNameError,
  } = useInput(user.firstName)
  const {
    value: lastName,
    bind: bindLastName,
    error: lastNameError,
    setError: setLastNameError,
  } = useInput(user.lastName)
  const {
    value: email,
    bind: bindEmail,
    error: emailError,
    setError: setEmailError,
  } = useInput(user.email)
  const { value: phone, bind: bindPhone } = useInput(user.phone)
  const { value: location, setValue: setLocation } = useInput(user.location)
  const { value: role, bind: bindRole } = useInput(user.role)

  const emailIsValid = useEmailValidator(email as string)
  const [personalInfoIsDirty, setPersonalInfoIsDirty] = useState(false)
  const lastActive = useDateFormatter(user.lastActive, 'MM-DD-YYYY')
  const createdAt = useDateFormatter(user.createdAt, 'MM-DD-YYYY')

  const userIsAUser = user.role === 'User'

  useEffect(() => {
    if (
      firstName !== user.firstName ||
      lastName !== user.lastName ||
      email !== user.email ||
      phone !== user.phone ||
      role !== user.role ||
      location !== user.location
    ) {
      setPersonalInfoIsDirty(true)
    } else {
      setPersonalInfoIsDirty(false)
    }
  }, [firstName, lastName, email, phone, role, location, user])

  const formIsValid = () => {
    let ready = true

    if (firstName === '') {
      ready = false
      setFirstNameError('Please enter your first name')
    } else {
      setFirstNameError('')
    }

    if (lastName === '') {
      ready = false
      setLastNameError('Please enter your last name')
    } else {
      setLastNameError('')
    }

    if (email === '') {
      ready = false
      setEmailError('Please enter your last name')
    } else if (!emailIsValid) {
      ready = false
      setEmailError('Please enter a valid email')
    } else if (typeof email === 'string' && !email.includes('@hexagon.com')) {
      ready = false
      setEmailError('Email must be a Hexagon account')
    } else {
      setEmailError('')
    }

    return ready
  }

  const handleUpdate = () => {
    if (formIsValid()) {
      const updatedUser = {
        _id: user._id,
        firstName,
        lastName,
        email,
        phone,
        location,
        role,
      }

      dispatch(updateAdminUserInfo(updatedUser))
    }
  }

  const handleDeactivateClick = () => {
    user.status = 'Deactivated'
    setShowDeactivateModal(true)
  }

  const handleDeleteClick = () => {
    setShowDeleteModal(true)
  }

  return (
    <div className="form-container">
      <h5 className="m-b-0">
        {user.firstName} {user.lastName}
      </h5>
      <Grid container spacing={2} className="m-t-24">
        <Grid item xs={6}>
          <p className="m-b-5">Joined: {user.createdAt ? createdAt : 'N/A'}</p>
          <p>Last active: {user.lastActive ? lastActive : 'N/A'}</p>
        </Grid>

        <Grid item xs={6} className="m-l--12">
          {(userInfo.role === 'Manager' || userInfo.role === 'Support') && (
            <PasswordResetLink email={user.email} />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={2} className="m-t-16">
        <Grid item xs={6}>
          <TextInput
            name="firstName"
            label="First name"
            bindValue={bindFirstName}
            error={firstNameError}
            disabled={userInfo.role === 'AE' || userIsAUser}
          />
        </Grid>

        <Grid item xs={6}>
          <TextInput
            name="lastName"
            label="Last name"
            bindValue={bindLastName}
            error={lastNameError}
            disabled={userInfo.role === 'AE' || userIsAUser}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            name="email"
            label="Email"
            bindValue={bindEmail}
            error={emailError}
            disabled={userInfo.role === 'AE' || userIsAUser}
          />
        </Grid>

        <Grid item xs={6}>
          <TextInput
            type="tel"
            name="phone"
            label="Phone number"
            bindValue={bindPhone}
            disabled={userInfo.role === 'AE' || userIsAUser}
          />
        </Grid>

        {userIsAUser ? (
          <>
            <Grid item xs={6}>
              <TextInput
                type="text"
                name="company"
                label="Company"
                value={
                  user.company && user.company.companyName
                    ? user.company.companyName
                    : ''
                }
                disabled={true}
              />
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
              <TextInput
                type="text"
                name="streetAddress"
                label="Address"
                value={
                  user.company && user.company.streetAddress
                    ? user.company.streetAddress
                    : ''
                }
                disabled={true}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                type="text"
                name="aptSuiteOther"
                label="Apt/Suite/Other"
                value={
                  user.company && user.company.aptSuiteOther
                    ? user.company.aptSuiteOther
                    : ''
                }
                disabled={true}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                type="text"
                name="city"
                label="City"
                value={
                  user.company && user.company.city ? user.company.city : ''
                }
                disabled={true}
              />
            </Grid>
            <Grid item xs={3}>
              <TextInput
                type="text"
                name="state"
                label="State"
                value={
                  user.company && user.company.state ? user.company.state : ''
                }
                disabled={true}
              />
            </Grid>
            <Grid item xs={3}>
              <TextInput
                type="text"
                name="zipCode"
                label="Zip code"
                value={
                  user.company && user.company.zipCode
                    ? user.company.zipCode
                    : ''
                }
                disabled={true}
              />
            </Grid>
            <Grid item xs={6}>
              <TextInput
                type="text"
                name="role"
                label="User role"
                value="User"
                disabled={true}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={6}>
              <SelectInput
                label="Role"
                name="role"
                options={[
                  { value: 'Manager', label: 'Manager' },
                  { value: 'AE', label: 'AE' },
                  { value: 'Support', label: 'Support' },
                ]}
                bindValue={bindRole}
                disabled={userInfo.role === 'AE'}
              />
            </Grid>
            <Grid item xs={6}>
              <AutoCompleteSelect
                name="location"
                label="Location"
                options={locations}
                value={location as string}
                setValue={setLocation}
                minWidth={256}
                disabled={userInfo.role === 'AE'}
              />
            </Grid>
          </>
        )}
      </Grid>

      <div className="flex space-between m-t-30">
        <div>
          {userInfo.role !== 'AE' &&
            !userIsAUser &&
            (user.status === 'Deactivated' ? (
              <Button
                className="text p-l-0 p-r-0"
                color="primary"
                onClick={handleDeleteClick}
              >
                Delete user
              </Button>
            ) : (
              <Button
                className="text p-l-0 p-r-0"
                color="primary"
                onClick={handleDeactivateClick}
              >
                Deactivate
              </Button>
            ))}
        </div>
        <div>
          {userIsAUser ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowEditUser(false)}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowEditUser(false)}
              >
                {userInfo.role === 'AE' ? 'Close' : 'Cancel'}
              </Button>
              {userInfo.role !== 'AE' && (
                <Button
                  variant="contained"
                  color="primary"
                  className="m-l-32"
                  disabled={!personalInfoIsDirty}
                  onClick={handleUpdate}
                >
                  Save
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default EditAdminUser
